import { Button, MenuItem, Stack } from '@mui/material';
import { useIsSCIMKeyGenerated } from 'api/configurations';
import { useGenerateScimKey } from 'api/scimKeys';
import CopyableTextField from 'components/shared/CopyableTextField';
import SplitMenuButton from 'components/shared/SplitMenuButton';
import { useDialogsContext } from 'contexts/Dialogs/DialogsContext';
import { ScimConfirmActionType } from 'contexts/Dialogs/ScimRevokeActionsDialog/ScimRevokeActionsDialog';
import {
	useCurrentMethod,
	useIdPConfigContext
} from 'contexts/IdPConfigContext';
import { useSSOConfigCurrentFieldsInitialized } from 'contexts/SSOConfigFieldsContext';
import React, { useCallback, useState } from 'react';

interface ISPSCIMTokenFieldProps {
	fieldName: string;
	displayName: string;
}

const SPSCIMTokenField: React.FC<ISPSCIMTokenFieldProps> = ({
	fieldName,
	displayName
}) => {
	const currentMethod = useCurrentMethod();
	const { isSubTenant } = useIdPConfigContext();
	const fieldsReady = useSSOConfigCurrentFieldsInitialized();
	const { openScimRevokeActionsDialog } = useDialogsContext();

	const [generatedKey, setGeneratedKey] = useState<string | null>(null);

	const { data: isKeyGeneratedData, refetch: refetchIsKeyGenerated } =
		useIsSCIMKeyGenerated(currentMethod);

	const generateScimKey = useGenerateScimKey();

	const handleRotate = useCallback(async () => {
		try {
			const res = await generateScimKey();
			if (res) {
				setGeneratedKey(res.cleartext);
			}
		} catch {
			// Error toast is shown in useGenerateScimKey
		}
	}, [generateScimKey]);

	const handleActionWithConfirmation = useCallback(
		async (actionType: ScimConfirmActionType) => {
			const { success, cleartext } =
				await openScimRevokeActionsDialog(actionType);
			if (!success) return;

			if (cleartext) {
				setGeneratedKey(cleartext);
			} else {
				setGeneratedKey(null);
			}
			refetchIsKeyGenerated();
		},
		[openScimRevokeActionsDialog, refetchIsKeyGenerated]
	);

	const isKeyGenerated = isKeyGeneratedData?.isGenerated || generatedKey;

	return (
		<Stack flexDirection="row" gap={1} alignItems="center" mt={2}>
			{isKeyGenerated && (
				<CopyableTextField
					size="small"
					name={fieldName}
					sx={{
						display: 'block',
						flexGrow: 1
					}}
					disabled={!fieldsReady || isSubTenant}
					label={displayName}
					value={generatedKey || '***************'}
					hideCopyButton={!generatedKey}
					slotProps={{
						input: {
							sx: {
								width: '100%'
							}
						}
					}}
				/>
			)}
			{isKeyGenerated ? (
				<SplitMenuButton
					variant="outlined"
					onClick={handleRotate}
					menuChildren={
						<>
							<MenuItem
								onClick={() => handleActionWithConfirmation('rotateAndRevoke')}
							>
								{intl.tr('scimConfig.key.rotateAndRevoke')}
							</MenuItem>
							<MenuItem onClick={() => handleActionWithConfirmation('revoke')}>
								{intl.tr('scimConfig.key.revoke')}
							</MenuItem>
						</>
					}
				>
					{intl.tr('scimConfig.key.rotate')}
				</SplitMenuButton>
			) : (
				<Button variant="outlined" onClick={handleRotate}>
					{intl.tr('scimConfig.key.generate')}
				</Button>
			)}
		</Stack>
	);
};

export default SPSCIMTokenField;
